/**
 * PublicRoot - A lightweight wrapper for public pages that don't require authentication.
 *
 * This component is used for public form pages (/f/:slug, /embed/forms/:slug) to avoid
 * loading unnecessary services, stores, and providers that are only needed for the
 * authenticated app experience.
 *
 * What's included:
 * - ThemeProvider (for consistent styling)
 * - QueryClientProvider (for data fetching)
 * - Toaster (for notifications)
 * - Desktop updater listener (Tauri main window only)
 *
 * What's excluded (that Root has):
 * - AuthProvider (no auth needed for public forms)
 * - SocketProvider (no realtime features)
 * - RealTimeInitializer (no realtime subscriptions)
 * - TimeEntryProvider (no time tracking)
 * - OrganizationContextInitializer (no org context)
 * - EntityTerminologyProvider (no custom terminology)
 * - Most Tauri-specific hooks (external links, route restore, etc.)
 * - TauriDragRegion, TauriTitleBarProvider, TauriFileDropProvider
 * - RouteTracker, PageRestorer, FocusWindowRestorer
 * - PopupOrganizationInitializer
 */

import { Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { queryClient } from '@/lib/queryClient';
import { TauriTitleBarShell } from '@/components/layout/TauriTitleBarShell';
import { useAutoUpdater } from '@/hooks/useAutoUpdater';
import { isPopupWindow, isTauri } from '@/lib/utils/tauri';

function PublicAutoUpdaterInitializer() {
  useAutoUpdater();
  return null;
}

export function PublicRoot() {
  const isDesktopMainWindow = isTauri() && !isPopupWindow();

  return (
    <ThemeProvider defaultTheme="light" storageKey="scrambl-ui-theme">
      <QueryClientProvider client={queryClient}>
        {isDesktopMainWindow && <PublicAutoUpdaterInitializer />}
        <TauriTitleBarShell className="flex flex-col h-full w-full min-h-screen overflow-auto bg-background">
          <div className="flex-1">
            <Outlet />
          </div>
        </TauriTitleBarShell>
        <Toaster position="bottom-right" />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
